import { leinWand, szene, kamera, renderer } from './index';
import * as THREE from 'three';
import { Tween, Easing } from '@tweenjs/tween.js';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls';
import Helfer from './Helfer';
import Terrain from './Terrain';
import ÄpfelSpawner from './ÄpfelSpawner';

interface spielerBlockListenElement {
	mesh: THREE.Mesh;
	richtungsVektor: THREE.Vector3;
}
export default class Spieler {
	// Attribute
	public readonly kopfFarbe = '#00f';
	public körperFarbe = 'green';
	private readonly blockGröße = 1;
	private readonly schrittGröße = 1;
	public bewegungsInterval = 750 * this.schrittGröße;
	public richtungsVektor = new THREE.Vector3(0, 0, -1);
	public vorherigeRichtungsVektoren = [this.richtungsVektor.clone(), this.richtungsVektor.clone()];
	public kameraStatus = {
		mussRotiertWerden: false,
		mussTeleportiertWerden: false,
		animiertGerade: false,
	}
	public kameraTween: any = {
		update() {}
	}

	// Mesh
	private readonly geometrie = new THREE.BoxGeometry(
		this.blockGröße,
		this.blockGröße,
		this.blockGröße
	);
	public material = new THREE.MeshBasicMaterial({
		color: this.körperFarbe
	});

	public kreeireSpielerMesh() {
		const mesh = new THREE.Mesh(this.geometrie, this.material);
		// Aktualisiere matrix manuell für mehr Effizienz
		mesh.matrixAutoUpdate = false;
		mesh.geometry.computeBoundingBox();
		return mesh;
	}

	public blockListe: Array<spielerBlockListenElement> = [
		{
			mesh: this.kreeireSpielerMesh(),
			richtungsVektor: this.richtungsVektor.clone()
		}
	];

	fügeBlockHinzu() {
		const mesh = this.kreeireSpielerMesh();
		const letzterBlock = this.blockListe[
			this.blockListe.length - 1
		];
		mesh.position.copy(
			letzterBlock.mesh.position.clone().add(this.richtungsVektor.clone().negate())
		);
		this.blockListe.push({
			mesh,
			richtungsVektor: letzterBlock.richtungsVektor
		});
		szene.add(mesh);
		mesh.updateMatrix();
	}
	// Bewegung
	public bewegeSpieler(guckeFürKollissionen: Function): void {
		// Entferne das letzte Block vom Spieler und füge es am Kopf hinzu
		const kopfVonSpielerPosition = this.blockListe[0].mesh.position.clone();
		const letzterSpielerBlock = this.blockListe.pop()!;
		letzterSpielerBlock.richtungsVektor.copy(this.richtungsVektor);
		// Animiere Kamera zum neuen  Kopf der Schlange
		kamera.position.copy(kopfVonSpielerPosition);
		const ziel = kopfVonSpielerPosition.clone().add(this.richtungsVektor.clone().multiplyScalar(this.schrittGröße))
		this.kameraTween = new Tween(kamera.position)
		.to({
			x: ziel.x,
			y: ziel.y,
			z: ziel.z
		}, this.bewegungsInterval)
		.easing(Easing.Linear.None)
		.onUpdate(() => {
			letzterSpielerBlock.mesh.position.copy(kamera.position);
			letzterSpielerBlock.mesh.updateMatrix();
		})
		.onComplete(() => {
			this.kameraStatus.animiertGerade = false;
			this.blockListe.unshift(letzterSpielerBlock);
			guckeFürKollissionen();
		})
		.start();
		// Verhindert sonstige Kamera bewegungen, während sie bewegt wird
		this.kameraStatus.animiertGerade = true;
	}

	verarbeiteKollissionenMitSpieler(gameOver: Function) {
		for(let i = 1; i < this.blockListe.length; i++) {
			const spielerBlockBox = new THREE.Box3().setFromObject(this.blockListe[i].mesh);
			const spielerKopfBox = new THREE.Box3().setFromObject(this.blockListe[0].mesh);
			if(spielerKopfBox.containsBox(spielerBlockBox)) {
				gameOver();
			}
		}
	}

	// Steuerung
	public mausSteuerung = new PointerLockControls(kamera, leinWand);

	public aktivieresteuerung(terrain: Terrain) {
		this.mausSteuerung.lock();
		const oben = ['KeyW', 'ArrowUp'];
		const unten = ['KeyS', 'ArrowDown'];
		const links = ['KeyA', 'ArrowLeft'];
		const rechts = ['KeyD', 'ArrowRight'];

		document.addEventListener('keydown', (key) => {
			// Teste ob richtungsvektor parallel zur Schlange ist, damit man nicht in die Schlange rein rotiert
			if(!this.richtungsVektor.equals(this.blockListe[0].richtungsVektor))
				return;
			// Rotiere Schlange nicht, wenn es gerade durch einem Portal geht
			const position = this.blockListe[0].mesh.position;
			if((position.x <= -terrain.seitenAbstand + this.blockGröße && this.richtungsVektor.x === -1)
				|| (position.x >= terrain.breite - terrain.seitenAbstand - this.blockGröße && this.richtungsVektor.x === 1)
				|| (position.y <= -terrain.seitenAbstand + this.blockGröße && this.richtungsVektor.y === -1) 
				|| (position.y >= terrain.höhe - terrain.seitenAbstand - this.blockGröße && this.richtungsVektor.y === 1)
				|| (position.z <= -terrain.seitenAbstand + this.blockGröße && this.richtungsVektor.z === -1) 
				|| (position.z >= terrain.dicke - terrain.seitenAbstand - this.blockGröße && this.richtungsVektor.z === 1)) {
					return;
				}
			// Drehe Richtungsvektor um 90 Grad in verschieden Richtungen um Richtung zu wechseln
			const vorher = this.richtungsVektor.clone();
	
			switch (key.code) {
				case unten[0]:
				case unten[1]:
					Helfer.rotiereVektorNachOben(
						this.richtungsVektor,
						this.vorherigeRichtungsVektoren[0]
					);
					this.richtungsVektor.negate();
					if(kamera.up.y !== 1)
						kamera.up.negate()
					break;
				case oben[0]:
				case oben[1]:
					Helfer.rotiereVektorNachOben(
						this.richtungsVektor,
						this.vorherigeRichtungsVektoren[0]
					);
					break;
				case links[0]:
				case links[1]:
					Helfer.rotiereVektorNachRechts(
						this.richtungsVektor,
						this.vorherigeRichtungsVektoren[0]
					);
					this.richtungsVektor.negate();
					break;
				case rechts[0]:
				case rechts[1]:
					Helfer.rotiereVektorNachRechts(
						this.richtungsVektor,
						this.vorherigeRichtungsVektoren[0]
					);
					break;
				case 'KeyR': 
					this.rotiereKameraGeradeAus();
					break;
			}
			// Bewege die Kamera, wenn die Richtungs nicht gleich bleibt
			if(!vorher.equals(this.richtungsVektor)) {
				this.rotiereKameraGeradeAus();
			}
		});
	}

	// Kamera Rotation und Bewegung animieren mitfilfe von tween.js
	// https://stackoverflow.com/questions/14567712/how-to-animate-camera-lookat-using-three-js
	public rotiereKameraParallelZurSchlange(längeDerAnimation = this.bewegungsInterval) {
		// Kopf von Schlange + orientierung der Kamera
		const aktuellePosition = new THREE.Vector3();
		this.mausSteuerung.getDirection(aktuellePosition);
		aktuellePosition.add(this.blockListe[0].mesh.position);
		// Kopf von Schlange + Richtung der Schlange
		const ziel = this.blockListe[0].mesh.position.clone().add(this.richtungsVektor);
		this.kameraTween = new Tween(aktuellePosition)
		.to({
			x: ziel.x,
			y: ziel.y,
			z: ziel.z
		}, längeDerAnimation)
		.easing(Easing.Linear.None)
		.onUpdate(() => {
			kamera.lookAt(aktuellePosition);
		})
		.onComplete(() => {
			this.kameraStatus.mussRotiertWerden = false;
			this.kameraStatus.animiertGerade = false;
		})
		.start();
		// Verhindert Kamera bewegungen, während sie rotiert wird
		this.kameraStatus.animiertGerade = true;
	}

	public rotiereKameraGeradeAus() {
		this.richtungsVektor.round();
		kamera.up.round();
		this.kameraStatus.mussRotiertWerden = true;
		this.vorherigeRichtungsVektoren.push(this.richtungsVektor.clone());
		// Lösche alte Vektoren
		if(this.vorherigeRichtungsVektoren.length > 2) {
			this.vorherigeRichtungsVektoren.shift();
		}
	}
}
